import React, { useEffect, useState } from 'react';
import { useBusinessInfo, telHref } from '../hooks/useBusinessInfo';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone } from 'lucide-react';
import { setSeoMetadata, SEO_PRESETS } from '../lib/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ScheduleDemoPage = () => {
  // Phone comes from Settings -> Business Info, never a literal.
  const { phone } = useBusinessInfo();
  const phoneHref = telHref(phone);
  const [formData, setFormData] = useState({
    organization: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    products: [],
    contactMethod: 'both',
    notes: '',
    agreedToTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    setSeoMetadata({
      ...SEO_PRESETS.demo,
      // Business name is appended from General Settings by setSeoMetadata.
      title: 'Book a Free Consultation',
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          subject: `Consultation Request - ${formData.organization}`,
          message: `Organization: ${formData.organization}\nServices of Interest: ${formData.products.join(', ') || 'Not specified'}\nPreferred Contact: ${formData.contactMethod}\nProject Details: ${formData.notes}`,
          source: 'consultation_request',
        }),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen bg-bots-dark">
        <Header />
        <section className="pt-32 pb-20">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <CheckCircle className="w-24 h-24 text-green-500 mx-auto mb-8" />
            <h1 className="text-4xl font-bold text-white mb-6">Thank You!</h1>
            <p className="text-xl text-gray-300 mb-8">
              Your consultation request has been received. We will be in touch within one business day to arrange a time and start scoping your project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="px-8 py-4 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-500 transition-colors"
              >
                Return Home
              </Link>
              <Link
                to="/products"
                className="px-8 py-4 bg-bots-surface border border-gray-700 text-white font-bold rounded-full hover:bg-bots-accent transition-colors"
              >
                View Products
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bots-dark">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-bots-surface to-bots-dark">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Book Your <span className="text-[#FF6B00]">Free Consultation</span>
          </h1>
          <p className="text-xl text-[#dfddd6] max-w-3xl mx-auto">
            Tell us what you are trying to build. We will tell you what it takes &mdash; honestly,
            and before you spend anything.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Benefits */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-8">What to Expect</h2>
              <div className="space-y-6">
                {[
                  { title: 'A Real Conversation About Scope', desc: 'We walk through what you actually need, what can wait, and what is more complex than it looks. Often the useful answer is a smaller build.' },
                  { title: 'An Honest Estimate', desc: 'A timeline and a cost range with the reasoning attached, so you can see what drives the number instead of being handed one.' },
                  { title: 'A Look At What You Already Have', desc: 'Existing systems, data and tools you are paying for. Integrating is usually cheaper than replacing.' },
                  { title: 'No Pressure, No Obligation', desc: 'If we are not the right fit for the project, we will say so. You keep the plan either way.' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-green-500 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                      <p className="text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Contact Info */}
              <div className="mt-12 p-6 bg-bots-surface rounded-xl border border-gray-800">
                <h3 className="text-white font-semibold mb-4">Prefer to Talk?</h3>
                <a
                  href={phoneHref}
                  className="flex items-center text-blue-400 hover:text-blue-300 transition-colors text-lg"
                >
                  <Phone className="w-5 h-5 mr-3" />
                  {phone}
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="bg-bots-surface p-8 rounded-2xl border border-gray-800">
              <h2 className="text-2xl font-bold text-white mb-6">Request Your Consultation</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="schedule-demo-form">
                <input
                  type="text"
                  placeholder="Organization*"
                  required
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                  data-testid="demo-form-organization"
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="First Name*"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                    data-testid="demo-form-firstname"
                  />
                  <input
                    type="text"
                    placeholder="Last Name*"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                    data-testid="demo-form-lastname"
                  />
                </div>
                
                <div className="w-full" data-testid="demo-form-products">
                  <p className="text-[#888680] text-sm mb-3">What are you looking to build?</p>
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-[#FF6B00] uppercase tracking-widest">Build</p>
                    <div className="grid grid-cols-2 gap-2">
                      {['WEB APPLICATION','CRM / INTERNAL SYSTEM','E-COMMERCE','MOBILE APP','AI / AUTOMATION','WEBSITE REDESIGN'].map((service) => (
                        <label key={service} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm font-medium uppercase tracking-wide ${formData.products.includes(service) ? 'border-[#FF6B00] bg-[#FF6B00]/10 text-[#FF6B00]' : 'border-[#4a3520] bg-[#1a1c1b] text-[#dfddd6] hover:border-[#888680]'}`}>
                          <input
                            type="checkbox"
                            className="accent-[#FF6B00]"
                            checked={formData.products.includes(service)}
                            onChange={(e) => setFormData({ ...formData, products: e.target.checked ? [...formData.products, service] : formData.products.filter(p => p !== service) })}
                            data-testid={`consult-service-${service.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                          />
                          {service}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs font-semibold text-[#f8d47a] uppercase tracking-widest mt-3">Support &amp; Growth</p>
                    <div className="grid grid-cols-2 gap-2">
                      {['SEO & CONTENT','HOSTING & MAINTENANCE','INTEGRATIONS','TAKE OVER EXISTING PROJECT'].map((service) => (
                        <label key={service} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm font-medium uppercase tracking-wide ${formData.products.includes(service) ? 'border-[#f8d47a] bg-[#f8d47a]/10 text-[#f8d47a]' : 'border-[#4a3520] bg-[#1a1c1b] text-[#dfddd6] hover:border-[#888680]'}`}>
                          <input
                            type="checkbox"
                            className="accent-[#f8d47a]"
                            checked={formData.products.includes(service)}
                            onChange={(e) => setFormData({ ...formData, products: e.target.checked ? [...formData.products, service] : formData.products.filter(p => p !== service) })}
                            data-testid={`consult-service-${service.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                          />
                          {service}
                        </label>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      {['NOT SURE YET - NEED ADVICE'].map((opt) => (
                        <label key={opt} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm font-medium uppercase tracking-wide ${formData.products.includes(opt) ? 'border-[#888680] bg-[#888680]/10 text-white' : 'border-[#4a3520] bg-[#1a1c1b] text-[#888680] hover:border-[#888680]'}`}>
                          <input
                            type="checkbox"
                            className="accent-[#888680]"
                            checked={formData.products.includes(opt)}
                            onChange={(e) => setFormData({ ...formData, products: e.target.checked ? [...formData.products, opt] : formData.products.filter(p => p !== opt) })}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <span className="text-gray-400 text-sm">Contact me via:</span>
                  {['phone', 'email', 'both'].map((method) => (
                    <label key={method} className="flex items-center text-white cursor-pointer">
                      <input
                        type="radio"
                        name="contactMethod"
                        value={method}
                        checked={formData.contactMethod === method}
                        onChange={(e) => setFormData({ ...formData, contactMethod: e.target.value })}
                        className="mr-2"
                      />
                      {method.charAt(0).toUpperCase() + method.slice(1)}
                    </label>
                  ))}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="email"
                    placeholder="Email*"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                    data-testid="demo-form-email"
                  />
                  <input
                    type="tel"
                    placeholder="Phone*"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                    data-testid="demo-form-phone"
                  />
                </div>
                
                <textarea
                  placeholder="What are you trying to build or fix? Existing systems, deadlines, anything we should know..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 bg-bots-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none resize-none"
                  data-testid="demo-form-notes"
                />
                
                <label className="flex items-start text-gray-300 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    checked={formData.agreedToTerms}
                    onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
                    className="mr-3 mt-1"
                    data-testid="demo-form-terms"
                  />
                  <span>
                    I agree to the terms & conditions. By providing my phone number, I agree to receive communications from 123 Bots.
                  </span>
                </label>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-green-500 text-black font-bold rounded-full hover:bg-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="demo-form-submit"
                >
                  {isSubmitting ? 'Submitting...' : 'SCHEDULE MY DEMO'}
                </button>
                
                {submitStatus === 'error' && (
                  <p className="text-red-400 text-center">Something went wrong. Please try again or call us directly.</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ScheduleDemoPage;
